import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Banner from '@zenbusiness/zui/Banner';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import {
  ZB_BANKING_SUNSET_PH3,
  ZB_BANKING_SUNSET_PH3_HIGH_RISK_CUSTOMERS
} from '#client/constants/experimentsFlags';
import { FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import useBankingPath from '#client/hooks/useBankingPath';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import {
  defaultPayloadBannerDisplayed,
  defaultPayloadButtonPressed
} from '#client/hooks/useTrackEvent';
import { BANKING_TRANSITION_SEGMENT } from '#client/pages/BankingTransition/constants';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';

const DEFAULT_COPY = {
  title: 'ZenBusiness Banking will shut down Nov 30. Deposits will be unavailable after Nov 1.',
  subtitle:
    'We have new banking options for you. Please review so you can create a new account ' +
    'and transfer smoothly.'
};

// High-risk customers have their accounts closed on Oct 2 instead of Nov 30.
const HIGH_RISK_COPY = {
  title: 'ZenBusiness Banking will shut down Oct 2. We have options for you.',
  subtitle:
    'We have new banking options for you with additional benefits and sign up bonuses. ' +
    'Create a new account with a preferred partner or another bank to transfer your funds.'
};

export default function BankingTransitionBanner() {
  const [isBankingSunsetPh3] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);
  const [isHighRiskCustomer] = useFeatureFlag(ZB_BANKING_SUNSET_PH3_HIGH_RISK_CUSTOMERS);
  const { entitlements } = useBusinessEntity();
  const bankingPath = useBankingPath();
  const trackFaqLinkClick = useButtonPressedCallback({
    ...defaultPayloadButtonPressed,
    elementName: 'banking_transition_banner_faq_link'
  });

  if (!isBankingSunsetPh3 || !entitlements?.has('banking')) {
    return null;
  }

  const transitionPath = `${bankingPath}/${BANKING_TRANSITION_SEGMENT}`;
  const copy = isHighRiskCustomer ? HIGH_RISK_COPY : DEFAULT_COPY;

  return (
    <BannerWrapperStyled>
      <Banner
        clickstreamProps={{
          bannerDisplayed: {
            ...defaultPayloadBannerDisplayed,
            bannerType: 'alert',
            elementName: 'banking_transition_banner'
          }
        }}
        slotProps={{
          primaryAction: {
            label: 'Confirm and Choose',
            to: transitionPath,
            clickstreamProps: {
              ...defaultPayloadButtonPressed,
              elementName: 'banking_transition_banner_cta'
            }
          }
        }}
        slots={{
          startIcon: (
            <SharedAsset
              alt=""
              color="#000000"
              src="/s2/icon/warning-sign-fill.svg"
              width={34}
            />
          )
        }}
        subtitle={
          <>
            {copy.subtitle}{' '}
            <Link
              color="inherit"
              href={FAQ_OVERVIEW_URL}
              onClick={trackFaqLinkClick}
              rel="noreferrer"
              target="_blank"
            >
              See FAQs for details.
            </Link>
          </>
        }
        title={copy.title}
        variant="alert"
      />
    </BannerWrapperStyled>
  );
}
